/**
 * Social login utility functions
 */

/**
 * Redirect to social login provider
 * @param {string} provider - The social provider (facebook, google, twitter, etc)
 * @param {string} baseURL - The base API URL
 * @param {boolean} isAuthenticated - Whether the user is already authenticated
 * @param {string} token - The authentication token if user is authenticated
 * @returns {Object} - Status of the redirect
 */
export function openSocialLoginPopup(provider, baseURL, isAuthenticated, token) {
  console.log(`Redirecting to social login provider: ${provider}`);
  
  // Store the current URL to return to after authentication
  const returnUrl = window.location.href;
  localStorage.setItem('socialLoginReturnUrl', returnUrl);
  
  // Store the provider for tracking purposes
  localStorage.setItem('socialLoginProvider', provider);
  
  // Build the URL with authentication token if user is logged in
  const url = baseURL +
    "/social/" +
    provider +
    (isAuthenticated ? "?_clientId=1&_bearer=" + token : "");
  
  console.log(`Social login URL: ${url}`);
  
  try {
    // Save timestamp to track when the redirect happened
    localStorage.setItem('socialLoginTimestamp', Date.now().toString());
    
    // Redirect to the authentication URL
    window.location.href = url;
    
    return {
      success: true,
      redirected: true
    };
  } catch (e) {
    console.error("Failed to redirect to authentication URL:", e);
    return {
      success: false,
      error: "REDIRECT_ERROR",
      message: "Could not redirect to authentication for " + provider,
      details: e
    };
  }
}

/**
 * Set up message listener for social login popup
 * @param {Function} callback - The callback function to handle received messages
 * @returns {Function} - Function to remove the event listener
 */
export function setupSocialLoginListener(callback) {
  const messageHandler = (event) => {
    if (event?.data?.isVowEvent === undefined) {
      return;
    }
    
    callback(event);
  };
  
  window.addEventListener("message", messageHandler);
  
  return () => {
    window.removeEventListener("message", messageHandler);
  };
}

/**
 * Check if we're returning from a social login redirect
 * @returns {boolean} - Whether we're returning from a social login
 */
export function checkSocialLoginReturn() {
  const provider = localStorage.getItem('socialLoginProvider');
  const timestamp = localStorage.getItem('socialLoginTimestamp');
  
  if (!provider || !timestamp) {
    return false;
  }
  
  // Check if we're on the auth-callback page
  if (window.location.pathname === '/auth-callback') {
    return true;
  }
  
  // Check if the redirect happened recently (within 5 minutes)
  const redirectTime = parseInt(timestamp, 10);
  const currentTime = Date.now();
  const fiveMinutesInMs = 5 * 60 * 1000;
  
  if (currentTime - redirectTime < fiveMinutesInMs) {
    return true;
  }
  
  return false;
}

/**
 * Clean up social login storage data
 */
export function cleanupSocialLoginData() {
  localStorage.removeItem('socialLoginProvider');
  localStorage.removeItem('socialLoginTimestamp');
  localStorage.removeItem('socialLoginReturnUrl');
}
